import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/config/company";
import { EASE } from "@/components/KineticLines";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-300 ${
          scrolled ? "border-white/10 bg-steel/85" : "border-transparent bg-steel/40"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 md:h-20 md:px-10 lg:px-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src="/logo-mark.png"
              alt="MMC S.r.l. — Carpenteria Metallica"
              className="h-9 w-auto md:h-11"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="hidden border-l border-white/15 pl-3 font-tech text-[9px] uppercase leading-tight tracking-[0.3em] text-white/50 sm:block">
              S.r.l.
              <br />
              Carpenteria
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `font-tech text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-molten" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contatti"
              data-testid="nav-cta"
              className="bg-molten px-5 py-2.5 font-tech text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-molten-hot"
            >
              Preventivo
            </Link>
          </nav>

          <button
            data-testid="nav-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            className="flex size-11 items-center justify-center border border-white/15 text-white transition-colors duration-300 hover:border-molten hover:text-molten lg:hidden"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col bg-ink/[0.985] pt-24 backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-1 flex-col justify-center px-6">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, ease: EASE, delay: 0.08 + i * 0.06 }}
                  className="border-b border-white/10"
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`nav-mobile-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-baseline gap-4 py-4 font-display text-4xl font-semibold uppercase tracking-tight ${
                        isActive ? "text-molten" : "text-white"
                      }`
                    }
                  >
                    <span className="font-tech text-xs text-white/35">0{i + 1}</span>
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-10">
              <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">
                {COMPANY.phoneDisplay} — {COMPANY.emailDisplay}
              </p>
              <p className="mt-2 font-tech text-[10px] uppercase tracking-[0.3em] text-molten">{COMPANY.sector}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
